// floating-overlay.service.ts (changes: FloatingRef + createWithFactory)
import { ApplicationRef, ComponentRef, Injectable, Injector, Type, createComponent, ViewContainerRef, NgZone } from '@angular/core';
import { FloatingHostComponent } from './floating-host.component';
import { Subject } from 'rxjs';

export interface FloatingRef<T = any> {
  id: string;
  hostRef: ComponentRef<FloatingHostComponent>;
  contentRef?: ComponentRef<T>;
  // NEW: expose the host's ViewContainerRef so callers can create components directly
  container?: ViewContainerRef;
  show(): void;
  hide(): void;
  destroy(): void;
  setPosition(x:number,y:number): void;
  getPosition(): {x:number,y:number};
}

@Injectable({ providedIn: 'root' })
export class FloatingOverlayService {
  private instances = new Map<string, FloatingRef>();

  constructor(private appRef: ApplicationRef, private injector: Injector, private ngZone: NgZone) {}

  /**
   * createWithFactory
   * @param factory callback invoked with the host ViewContainerRef so the caller can create any component/template
   *                (factory may return a ComponentRef or void or a Promise resolving to one)
   * @param options optional left/top/persistenceKey/snapToEdge
   */
  async createWithFactory<T = any>(
    factory: (container: ViewContainerRef) => ComponentRef<T> | void | Promise<ComponentRef<T> | void>,
    options?: { id?: string, left?: number, top?: number, persistenceKey?: string, snapToEdge?: boolean, corner?: 'top-right', boundsSelector?: string }
  ): Promise<FloatingRef<T>> {
    const id = options?.id ?? `flo-${Math.random().toString(36).slice(2,8)}`;
    if (this.instances.has(id)) return this.instances.get(id)! as FloatingRef<T>;

    // create host component
    const hostRef = createComponent(FloatingHostComponent, { environmentInjector: (this.injector as any) });
    this.appRef.attachView(hostRef.hostView);
    const hostEl = hostRef.location.nativeElement as HTMLElement;
    document.body.appendChild(hostEl);

    if (typeof options?.snapToEdge === 'boolean') hostRef.instance.snapToEdge = options!.snapToEdge!;
    if (options?.persistenceKey) hostRef.instance.persistenceKey = options!.persistenceKey;
    // anchorRight re-measures on every size change (via ResizeObserver, set up in the
    // component's own ngAfterViewInit), so it stays correct even if real content is
    // injected or grows after this point — unlike a one-shot left/top position.
    if (options?.corner === 'top-right') hostRef.instance.anchorRight = 16;
    if (options?.boundsSelector) hostRef.instance.boundsSelector = options.boundsSelector;

    const floatingRef: FloatingRef<T> = {
      id,
      hostRef,
      contentRef: undefined,
      container: undefined, // will be set once host emits onReady
      show: () => hostRef.instance.show(),
      hide: () => hostRef.instance.hide(),
      destroy: () => {
        try { this.appRef.detachView(hostRef.hostView); } catch(e) {}
        try { hostRef.destroy(); } catch(e) {}
        if (floatingRef.contentRef) {
          try { this.appRef.detachView(floatingRef.contentRef.hostView); } catch(e) {}
          try { floatingRef.contentRef.destroy(); } catch(e) {}
        }
        this.instances.delete(id);
      },
      setPosition: (x,y) => hostRef.instance.setPosition(x,y),
      getPosition: () => hostRef.instance.getPosition()
    };

    this.instances.set(id, floatingRef);

    const contentCreated = new Promise<ComponentRef<T> | void>((resolve) => {
      const sub = hostRef.instance.onReady.subscribe(async (vcRef: ViewContainerRef) => {
        floatingRef.container = vcRef;
  
        try {
          // ensure factory executes inside Angular zone so CD runs normally
          const maybeRef = await this.ngZone.run(() => factory(vcRef));
          if (maybeRef) {
            // IMPORTANT: if factory used vcRef.createComponent(...) the view is already attached.
            // Track the returned ComponentRef so we can destroy it later.
            floatingRef.contentRef = maybeRef as ComponentRef<T>;
  
            // If the factory for some reason created the component without using the vcRef
            // (not recommended), we attach the view as fallback:
            const hostView = (maybeRef as ComponentRef<any>).hostView;
            const alreadyAttached = this.appRef.components.some(c => c.hostView === hostView);
            if (!alreadyAttached) {
              try { this.appRef.attachView(hostView); } catch (e) { /* swallow */ }
            }
          }
        } catch (e) {
          console.error('FloatingOverlayService: factory threw', e);
        } finally {
          sub.unsubscribe();
          resolve(floatingRef.contentRef);
        }
      });
    });
  
    await contentCreated;

    // set initial position if provided (corner positioning is already handled via
    // anchorRight above, applied in the component's own ngAfterViewInit)
    if (typeof options?.left === 'number' && typeof options?.top === 'number') {
      hostRef.instance.setPosition(options.left!, options.top!);
    }

    // now floatingRef.container is available (if you need it)
    return floatingRef;
  }

  get(id: string) { return this.instances.get(id) ?? null; }

  destroy(id: string) {
    const inst = this.instances.get(id);
    if (!inst) return;
    inst.destroy();
  }

  destroyAll() {
    Array.from(this.instances.keys()).forEach(id => this.destroy(id));
  }
}
